import type { ErrorBoundaryProps } from "expo-router"
import { useSegments } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"
import { ScrollView, StyleSheet } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

import { ENVIRONMENT } from "@/base/environment"
import { getAppViewFromState } from "@/base/load-app"
import { SignOutButton } from "@/components/Auth"
import CreateSupportTicketForm from "@/components/CreateSupportTicketForm"
import { FarmHqLogo } from "@/components/FarmHqLogo"
import { FocusTrackingStatusBar } from "@/components/FocusTrackingStatusBar"
import { getUserIsAdminFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AlertDialog,
  AlertDialogScrollView,
  AppText,
  Button,
  Column,
  Divider,
  Heading,
  Paper,
  Row,
  SIZES,
  SPACING,
} from "@/theme"
import i18n from "@/translations/i18n"
import * as Sentry from "@sentry/react-native"
import { DevOnly } from "@/components/makeFilterComponent"
import { SeedDatabaseOptionsForm } from "@/components/SeedDatabaseOptionsForm"
import { logger } from "@/base/logger"

function DebugInfo({ stack }: { stack: string }) {
  const pathname = useSegments().join("/")
  const appView = useRootSelector(getAppViewFromState)
  return (
    <ScrollView>
      <Paper px={"$4"} py={"$2"}>
        <AppText>{"Admin Only"}</AppText>
        <Divider />
        <AppText>{ENVIRONMENT.targetDatabaseName}</AppText>
        <AppText>{`Backend: ${ENVIRONMENT.apiUrl}`}</AppText>
        <AppText>{`Route: ${pathname}`}</AppText>
        <AppText>{appView}</AppText>
        <AppText>{"Stack"}</AppText>
        <AppText>{stack}</AppText>
      </Paper>
    </ScrollView>
  )
}

function SeedDbDialog() {
  const [isOpen, setIsOpen] = React.useState(false)
  const onCloseSeedDatabaseDialog = () => setIsOpen(false)

  return (
    <React.Fragment>
      <Button text={"Seed Db"} onPress={() => setIsOpen(true)} />
      <AlertDialog
        isOpen={isOpen}
        titleElement={"Seed Database"}
        onClose={onCloseSeedDatabaseDialog}
      >
        <AlertDialogScrollView>
          <SeedDatabaseOptionsForm onClose={onCloseSeedDatabaseDialog} />
        </AlertDialogScrollView>
      </AlertDialog>
    </React.Fragment>
  )
}

const TIMEOUT_SECONDS = 1

function AutoRetry({
  isEnabled,
  retry,
}: ErrorBoundaryProps & {
  isEnabled: boolean
}) {
  React.useEffect(() => {
    // Maybe use interval here?
    let timeout: NodeJS.Timeout | undefined
    if (isEnabled) {
      timeout = setTimeout(() => {
        retry().catch((e) => {
          logger.error(e)
        })
      }, TIMEOUT_SECONDS * 1000)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [isEnabled, retry])
  return null
}
export function ScreenErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  const { t } = useTranslation("errors")
  const isAdmin = useRootSelector(getUserIsAdminFromState)
  const [showSupport, setShowSupport] = React.useState(false)

  React.useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      retry().catch((e) => {
        logger.error(e)
      })
    }, TIMEOUT_SECONDS * 1000)
    return () => {
      clearTimeout(timeout)
    }
  }, [retry])

  return (
    <SafeAreaView style={styles.root}>
      <FocusTrackingStatusBar />
      <AlertDialog
        isOpen={showSupport}
        titleElement={i18n.t("support:createSupportTicketTitle")}
        onClose={() => setShowSupport(false)}
      >
        <AlertDialogScrollView>
          <CreateSupportTicketForm onClose={() => setShowSupport(false)} />
        </AlertDialogScrollView>
      </AlertDialog>
      <Column maxW={"$lg"} mb={"$4"} mx={"auto"} p={"$4"} space={"$4"}>
        <Row flexWrap={"nowrap"} space={"$2"}>
          <FarmHqLogo name={"farmhq-leaf-iso.png"} resizeMode={"contain"} />
          <Heading style={styles.heading} variant={"h2"}>
            {t("errorBoundaryTitle")}
          </Heading>
        </Row>
        <Divider my={"$2"} />
        <AppText>{t("loadErrorMessage")}</AppText>
        <Button text={t("retryButton")} onPress={retry} />
        <Row>
          <Button
            IconComponent={"Comment"}
            style={styles.buttons}
            text={t("contactUs")}
            variant={"outline"}
            onPress={() => setShowSupport(true)}
          />
          <SignOutButton
            style={styles.buttons}
            variant={"outline"}
            onSuccess={retry}
          />
        </Row>
        <DevOnly>
          <SeedDbDialog />
          <AutoRetry error={error} isEnabled={false} retry={retry} />
        </DevOnly>
      </Column>
      {isAdmin || __DEV__ ? (
        <React.Fragment>
          <Divider />
          <DebugInfo stack={JSON.stringify(error.stack, null, 2)} />
        </React.Fragment>
      ) : null}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  buttons: {
    flexGrow: 1,
    marginHorizontal: SPACING.$1,
    marginVertical: SPACING.$2,
    maxWidth: SIZES.$sm,
    minWidth: 200,
  },
  heading: {
    flexGrow: 1,
  },
  root: {
    flex: 1,
  },
})
